import {
  Box,
  Breadcrumbs,
  Button,
  FormLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import toast from "react-hot-toast";
import {
  addCompanyDetail,
  fetchCompanyDetail,
  updateCompanyDetail,
} from "../config/api";
import { Card } from "../components/ui/Card";
import Title from "../components/ui/Title";
import { StyledSeperator } from "../components/ui/Seperator";
import { StyledInput } from "../components/ui/StyledInput";
import usePincode from "../hooks/usePincode";
import {
  getBillingAddres,
  submitBillingAddres,
  updateBillingAddres,
} from "../components/features/setUp/setupApi";
import { StyledButton } from "../components/ui/StyledButton";

export interface BillingFormData {
  address_line_1: string;
  address_line_2: string;
  pincode: string;
  city: string;
  state: string;
  mobile: string;
}

const BillingAddress = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<BillingFormData>({
    address_line_1: "",
    address_line_2: "",
    pincode: "",
    city: "",
    state: "",
    mobile: "",
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;

    if (name === "mobile") {
      event.target.value = onlyNumber(event, 10);
    }
    if (name === "pincode") {
      event.target.value = onlyNumber(event, 6);
    }
    setFormData({ ...formData, [name]: event.target.value });
  };

  const onlyNumber = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    limit: number
  ) => event.target.value.replace(/[^0-9]/g, "").slice(0, limit);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isEdit) {
      submitBillingAddres(formData)
        .then(() => {
          toast.success("Billing Address Added");
          setFormData({
            address_line_1: "",
            address_line_2: "",
            pincode: "",
            city: "",
            state: "",
            mobile: "",
          });
        })
        .catch((error) => {
          console.log("BILLING ADDRESS", error);
        });
    } else {
      updateBillingAddres(formData)
        .then(() => {
          toast.success("Billing Address Upated");
        })
        .catch((error) => {
          console.log("BILLING ADDRESS", error);
        });
    }
  };

  useEffect(() => {
    if (
      formData.address_line_1 == "" ||
      formData.address_line_2 == "" ||
      formData.city == "" ||
      formData.mobile == "" ||
      formData.pincode == "" ||
      formData.state == ""
    ) {
      setCanSubmit(false);
    } else {
      setCanSubmit(true);
    }
  }, [formData]);

  useEffect(() => {
    getBillingAddres()
      .then((data) => {
        let billingAddress = data?.data?.data;

        if (billingAddress) {
          setIsEdit(true);
          setFormData(billingAddress);
        }
      })
      .catch((error) => console.log("BILLING ADDRESS GET", error));
  }, []);

  const { city, state } = usePincode(formData.pincode);

  useEffect(() => {
    const cloneFormData = { ...formData };
    cloneFormData.city = city;
    cloneFormData.state = state;
    setFormData(cloneFormData);
  }, [formData]);

  return (
    <>
      <Box
        sx={{
          marginBottom: "20px",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <Link to="/settings">
            <Typography color="text.primary" fontWeight={600}>
              Settings
            </Typography>
          </Link>
          <Typography color="text.primary">Billing</Typography>
          <Typography color="text.primary">Billing Invoice</Typography>
        </Breadcrumbs>
      </Box>
      <Card>
        <Box
          sx={{
            margin: "10px 0",
          }}
        >
          <Box>
            <Grid container justifyContent="space-between" alignItems="center">
              <Title title="Billing Address" />
              <StyledSeperator
                style={{
                  margin: "10px",
                  width: "100%",
                }}
              />
            </Grid>

            <form onSubmit={handleSubmit}>
              <Grid container mt={2}>
                <Grid item xs={12} md={6} gap={4}>
                  <Grid item xs={12} md={12}>
                    <FormLabel>
                      <Typography fontSize={15}>Address Line 1</Typography>
                    </FormLabel>
                    <StyledInput
                      fullWidth
                      onChange={handleInputChange}
                      placeholder="Address Line 1"
                      name="address_line_1"
                      value={formData.address_line_1}
                    />
                  </Grid>

                  <Grid item xs={12} md={12} marginTop={2}>
                    <FormLabel>
                      <Typography fontSize={15}>Address Line 2</Typography>
                    </FormLabel>
                    <StyledInput
                      onChange={handleInputChange}
                      placeholder="Address Line 2"
                      fullWidth
                      name="address_line_2"
                      value={formData.address_line_2}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} marginTop={2}>
                    <FormLabel>
                      <Typography fontSize={15}>Mobile Number</Typography>
                    </FormLabel>
                    <StyledInput
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="Mobile Number"
                      name="mobile"
                      value={formData.mobile}
                    />
                  </Grid>

                  <Grid item xs={12} md={12} marginTop={2}>
                    <FormLabel>
                      <Typography fontSize={15}>Pincode</Typography>
                    </FormLabel>
                    <StyledInput
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="Pincode"
                      name="pincode"
                      value={formData.pincode}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} marginTop={2}>
                    <FormLabel>
                      <Typography fontSize={15}>City</Typography>
                    </FormLabel>
                    <StyledInput
                      disabled
                      fullWidth
                      placeholder="City"
                      name="city"
                      value={formData.city}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} marginTop={2}>
                    <FormLabel>
                      <Typography fontSize={15}>State</Typography>
                    </FormLabel>
                    <StyledInput
                      disabled
                      fullWidth
                      placeholder="State"
                      name="state"
                      value={formData.state}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} marginTop={2}>
                    {(!isEdit && (
                      <StyledButton
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!canSubmit}
                      >
                        Save
                      </StyledButton>
                    )) || (
                      <StyledButton
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!canSubmit}
                      >
                        Update
                      </StyledButton>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default BillingAddress;
